/** Chip stack beside player seat on the rail */
import { motion } from 'framer-motion';
import ChipStack from 'components/ChipStack';
import type { SeatPositionId } from 'constants/seatLayout';
import { seatBetChipClass, seatBetMarkerClass } from 'constants/seatLayout';
import { useCurrency } from 'hooks/useCurrency';

interface SeatBetMarkerProps {
  amount: number;
  /** Pulse amount when a new bet lands */
  highlight?: boolean;
  /** Absolute % on table stage — used by chip-fly animations */
  x?: number;
  y?: number;
  /** Pin chips next to the player HUD on the rail */
  seatId?: SeatPositionId;
  anchored?: boolean;
  /** Beside card fan — left of hand (no absolute stage positioning) */
  inline?: boolean;
  /** Tighter single-chip layout for mobile seat bets */
  compact?: boolean;
  /** Chip above amount (portrait side seats) */
  stacked?: boolean;
}

export default function SeatBetMarker({
  amount,
  highlight = false,
  x,
  y,
  seatId,
  anchored = false,
  inline = false,
  compact = false,
  stacked = false,
}: SeatBetMarkerProps) {
  const { format } = useCurrency();
  if (amount <= 0) return null;

  /** Max 2 chips at seat — amount label carries the value for larger bets */
  const stackCount = compact || stacked
    ? 1
    : Math.min(2, Math.max(1, Math.ceil(amount / 40)));

  const pocketLayout = stacked
    ? 'tp-seat-bet-pocket-stacked flex-col items-center'
    : compact
      ? 'tp-seat-bet-pocket-compact flex-row items-center'
      : inline
        ? 'flex-row items-center'
        : anchored
          ? 'flex-col items-center'
          : 'flex-row items-center gap-1';

  const content = (
    <div className={`tp-seat-bet-pocket flex ${pocketLayout}`}>
      <ChipStack
        count={stackCount}
        size={compact || stacked ? 'xs' : 'sm'}
        layout={compact || stacked ? 'cluster' : 'stack'}
      />
      <motion.span
        className={`tp-seat-bet-amount shrink-0 rounded-md px-1.5 py-0.5 font-bold tabular-nums text-tp-gold ${stacked ? 'text-[8px] leading-tight' : compact ? 'text-[9px]' : inline ? 'text-[9px] sm:text-[10px]' : anchored ? 'text-[9px]' : 'text-[10px] sm:text-[11px]'} ${highlight ? 'tp-seat-bet-amount-highlight' : ''}`}
        animate={highlight ? { scale: [1, 1.18, 1], boxShadow: ['0 0 0 rgba(212,175,55,0)', '0 0 14px rgba(212,175,55,0.85)', '0 0 0 rgba(212,175,55,0)'] } : undefined}
        transition={{ duration: 0.75, ease: 'easeOut' }}
      >
        {format(amount)}
      </motion.span>
    </div>
  );

  if (inline) {
    return (
      <motion.div
        className="tp-seat-bet-inline shrink-0 self-center"
        initial={{ scale: 0.85, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        key={amount}
        transition={{ type: 'spring', stiffness: 420, damping: 22 }}
      >
        {content}
      </motion.div>
    );
  }

  if (anchored && seatId) {
    return (
      <motion.div
        className={`tp-seat-bet-chip-anchor ${seatBetChipClass(seatId)}`}
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        key={amount}
        transition={{ type: 'spring', stiffness: 420, damping: 22 }}
      >
        {content}
      </motion.div>
    );
  }

  if (x == null || y == null) return null;

  return (
    <div
      className={`tp-seat-bet-marker pointer-events-none absolute ${seatId ? seatBetMarkerClass(seatId) : ''}`}
      style={{ left: `${x}%`, top: `${y}%` }}
    >
      <motion.div
        initial={{ scale: 0.85, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        key={amount}
        transition={{ type: 'spring', stiffness: 420, damping: 22 }}
      >
        {content}
      </motion.div>
    </div>
  );
}
